import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthenticationGuard');
  }
  return context;
};

const AuthenticationGuard = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const publicRoutes = ['/homepage', '/blog-listing', '/blog-post-detail', '/admin-login', '/'];
  const adminRoutes = ['/admin-dashboard', '/admin-posts-management', '/admin-post-editor', '/admin-users-management', '/admin/users'];

  useEffect(() => {
    // Check current session on mount
    const checkSession = async () => {
      try {
        const session = await authService?.getCurrentSession();
        
        if (session?.user) {
          setUser(session?.user);
          const profile = await authService?.getUserProfile(session?.user?.id);
          setUserProfile(profile);
        } else {
          // No session = anonymous user, this is OK for public routes
          setUser(null);
          setUserProfile(null);
        }
      } catch (error) {
        console.error('Session check error:', error);
        // On error, treat as anonymous user
        setUser(null);
        setUserProfile(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();

    // Listen for auth changes
    const { data: { subscription } } = authService?.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        setUser(session?.user);
        authService?.getUserProfile(session?.user?.id)?.then(profile => {
          setUserProfile(profile);
        })?.catch(err => {
          console.error('Profile fetch error:', err);
          setUserProfile(null);
        });
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setUserProfile(null);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!isLoading) {
      const isAdminRoute = adminRoutes?.some(route => location?.pathname?.startsWith(route));
      
      // Only redirect if trying to access admin routes without authentication
      if (isAdminRoute) {
        if (!user) {
          navigate('/admin-login', { replace: true, state: { from: location?.pathname } });
        } else if (userProfile && userProfile?.role !== 'admin' && userProfile?.role !== 'editor') {
          navigate('/admin-login', { replace: true });
        }
      }
      // Public routes are accessible to everyone (user or anonymous)
    }
  }, [user, userProfile, isLoading, location?.pathname, navigate]);

  const signOut = async () => {
    try {
      await authService?.signOut();
      setUser(null);
      setUserProfile(null);
      navigate('/admin-login', { replace: true });
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const value = {
    user,
    userProfile,
    isAuthenticated: !!user,
    isAdmin: userProfile?.role === 'admin',
    isEditor: userProfile?.role === 'editor',
    isAdminOrEditor: userProfile?.role === 'admin' || userProfile?.role === 'editor',
    isLoading,
    signOut
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthenticationGuard;